<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue';
import { ORBIT_FRACTION, useOrbitingWedge } from './utils/useOrbitingWedge';
import {
  BUTTON_CENTER as CENTER,
  PIE_RADIUS,
  WEDGE_CIRCUMFERENCE as CIRCUMFERENCE,
  WEDGE_RADIUS,
  type ZeroMeasurementButtonPhase,
} from './utils/zeroMeasurementStartButton';

/**
 * The pie in the hub of the zero measurement start button: a solid white
 * disc with a wedge on it, drawn as an SVG group into the button's SVG.
 *
 * Idle, the wedge is the remaining share of the latest zero measurement's
 * validity, light grey, shifted to end at 12 o'clock, so the gap (the elapsed
 * share) sweeps clockwise from 12 like a clock hand as time passes; empty
 * without a valid one. Measuring, the wedge is the done share in the
 * particles' pale blue, starting at 12, so it fills clockwise. Until the
 * first percent it is a small slice of the pie orbiting it, so the empty pie
 * still reads as working; at the first percent it turns on to 12 while its
 * width morphs to the progress (see useOrbitingWedge). Succeeded, the full
 * wedge turns a pale green. A drop of the wedge (the start of a run empties
 * it, Repeat) snaps instead of shrinking; growth glides. A hairline at the
 * pie's edge keeps the hub readable even with an empty pie.
 *
 * Inviting (idle, pressable, no valid zero measurement), a soft blue glow
 * breathes in the center of the empty pie, under the play icon: a radial
 * gradient swelling from a small faint core to fill the pie and sinking
 * back, like a speaker's light under sound. The pie asks to be pressed.
 */
const props = defineProps<{
  phase: ZeroMeasurementButtonPhase;
  /** Share of the pie the wedge covers, 0 to 1. */
  fraction: number;
  /** The button asks to be pressed; only honoured while idle. */
  inviting?: boolean;
}>();

const measuring = computed(() => props.phase === 'measuring');
const fraction = computed(() => Math.min(1, Math.max(0, props.fraction)));
const orbiting = computed(() => measuring.value && fraction.value <= 0);
const wedgeLength = computed(() => CIRCUMFERENCE * fraction.value);
const orbitDashArray = `${CIRCUMFERENCE * ORBIT_FRACTION} ${CIRCUMFERENCE}`;
const dashArray = computed(() =>
  orbiting.value ? orbitDashArray : `${wedgeLength.value} ${CIRCUMFERENCE}`,
);
const dashOffset = computed(() => (props.phase === 'idle' ? wedgeLength.value - CIRCUMFERENCE : 0));

const snap = ref(false);
watch(fraction, (next, previous) => {
  snap.value = next < previous;
});

const { angle, settleStyle } = useOrbitingWedge(orbiting, measuring);
const transform = computed(() => `rotate(${angle.value} ${CENTER} ${CENTER})`);

const inviting = computed(() => props.phase === 'idle' && !!props.inviting);
const glowGradientId = `zero-measurement-glow-${useId()}`;
</script>

<template>
  <g class="pie" :class="`pie--${phase}`">
    <circle class="pie__disc" :cx="CENTER" :cy="CENTER" :r="PIE_RADIUS" />
    <circle
      class="pie__wedge"
      :class="{ 'pie__wedge--snap': snap, 'pie__wedge--orbiting': orbiting }"
      :cx="CENTER"
      :cy="CENTER"
      :r="WEDGE_RADIUS"
      :stroke-width="PIE_RADIUS"
      :stroke-dasharray="dashArray"
      :stroke-dashoffset="dashOffset"
      :transform="transform"
      :style="settleStyle"
      data-testid="ZeroMeasurementWedge"
    />
    <template v-if="inviting">
      <defs>
        <radialGradient :id="glowGradientId">
          <stop offset="0%" class="pie__glow-core" />
          <stop offset="100%" class="pie__glow-rim" />
        </radialGradient>
      </defs>
      <circle
        class="pie__glow"
        :cx="CENTER"
        :cy="CENTER"
        :r="PIE_RADIUS"
        :fill="`url(#${glowGradientId})`"
        data-testid="ZeroMeasurementGlow"
      />
    </template>
    <circle class="pie__edge" :cx="CENTER" :cy="CENTER" :r="PIE_RADIUS" />
  </g>
</template>

<style lang="scss" scoped>
$wedge-glide: 0.6s;
$grow: 0.4s;
$measuring-tint: 30%;
$glow-period: 2.4s;
$glow-core-opacity: 0.6;
$glow-small: 0.4;
$glow-faint: 0.35;

.pie {
  &__disc {
    fill: #fff;
  }

  &__edge {
    fill: none;
    stroke: rgba(var(--v-theme-on-surface), 0.12);
    stroke-width: 1;
  }

  &__wedge {
    fill: none;
    stroke: rgb(var(--v-theme-primary-lighten-5));
    // The wedge shrinks smoothly on each clock tick; its colour cross-fades
    // with the button's grow when a run starts or ends. Settling after the
    // orbit, the inline transition stretches and eases the dash's glide to
    // the turn.
    transition:
      stroke-dasharray $wedge-glide ease,
      stroke-dashoffset $wedge-glide ease,
      stroke $grow ease;
  }

  &__wedge--snap {
    transition: none;
  }

  // Orbiting: the slice; its turn is written into the transform attribute
  // by the script, frame by frame, so it is not transitioned here.
  &__wedge--orbiting {
    transition: none;
  }

  // The same faint tint the particles are painted in (see
  // RTSystemPairRowParticleStream), so wedge and stream read as one flow.
  &--measuring &__wedge {
    stroke: color-mix(
      in srgb,
      rgb(var(--v-theme-info)) $measuring-tint,
      rgb(var(--v-theme-surface))
    );
  }

  // The inviting glow: the info blue, strongest in its center and gone at
  // its rim, breathing in size and strength. A CSS transform is safe here,
  // unlike on the wedge (see useOrbitingWedge): the glow is removed with
  // the invitation, so its animation is never cancelled on a live element.
  &__glow {
    transform-box: fill-box;
    transform-origin: center;
    animation: pie-glow $glow-period ease-in-out infinite;
  }

  &__glow-core {
    stop-color: rgb(var(--v-theme-info));
    stop-opacity: $glow-core-opacity;
  }

  &__glow-rim {
    stop-color: rgb(var(--v-theme-info));
    stop-opacity: 0;
  }

  // Succeeded: the full wedge in the same faint tint in green, so the whole
  // hub reads done, not just the check.
  &--succeeded &__wedge {
    stroke: color-mix(
      in srgb,
      rgb(var(--v-theme-success-lighten-5)) $measuring-tint,
      rgb(var(--v-theme-surface))
    );
  }
}

@media (prefers-reduced-motion: reduce) {
  .pie__wedge {
    transition: none;
  }
}

// One breath: small and faint, out to the pie's edge at full strength, back.
@keyframes pie-glow {
  0%,
  100% {
    transform: scale($glow-small);
    opacity: $glow-faint;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pie__glow {
    animation: none;
    transform: scale(0.7);
    opacity: 0.65;
  }
}
</style>
